// Bottom sheet с формой платежа по долгу. По умолчанию сумма = monthly_payment,
// период = первый просроченный месяц (или следующий, если просрочек нет),
// дата выполнения = сегодня. Использует MainButton как primary CTA.

import { useMemo, useState } from 'react';
import type { Debt } from '../api/types';
import { SheetCta } from './SheetCta';
import { useMainButton } from '../lib/use-main-button';
import { useViewCurrency } from '../lib/view-currency';

type Props = {
  debt: Debt;
  isPrepayment: boolean;
  onSubmit: (payload: {
    amountMinor: bigint;
    periodYm: string | null;
    occurredAt: string; // ISO datetime
  }) => void;
  onCancel: () => void;
  loading?: boolean;
};

// "30 000" / "30000" / "30000.50" → bigint в тийинах. null если невалидно.
function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

const MONTH_RU_FULL = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

function formatPeriodLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return `${MONTH_RU_FULL[m - 1] ?? m} ${y}`;
}

// Список периодов на выбор: все overdue + 3 следующих неоплаченных.
function buildPeriodOptions(debt: Debt): string[] {
  const overdues = debt.overdue_periods ?? [];
  const opts = new Set<string>(overdues);

  // Добавляем следующий по графику + ещё 2 запас.
  if (debt.next_due_date) {
    const [y, m] = debt.next_due_date.split('-').map(Number) as [number, number];
    for (let i = 0; i < 3; i++) {
      const totalMonths = (y - 0) * 12 + (m - 1) + i;
      const yy = Math.floor(totalMonths / 12);
      const mm = (totalMonths % 12) + 1;
      opts.add(`${yy}-${String(mm).padStart(2, '0')}`);
    }
  } else {
    // Без графика — 3 ближайших месяца от сегодня.
    const now = new Date();
    for (let i = 0; i < 3; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
      opts.add(d.toISOString().slice(0, 7));
    }
  }

  return Array.from(opts).sort();
}

export function PaymentSheet({
  debt,
  isPrepayment,
  onSubmit,
  onCancel,
  loading = false,
}: Props): JSX.Element {
  const { formatMoney } = useViewCurrency();
  const initialMajor = (BigInt(debt.monthly_payment_minor) / 100n).toString();
  const [amount, setAmount] = useState(initialMajor);

  const periodOptions = useMemo(() => buildPeriodOptions(debt), [debt]);
  const defaultPeriod = useMemo(() => {
    if (isPrepayment) return null;
    // Первый просроченный, либо первый из опций.
    return (debt.overdue_periods?.[0] ?? periodOptions[0]) ?? null;
  }, [debt, periodOptions, isPrepayment]);
  const [periodYm, setPeriodYm] = useState<string | null>(defaultPeriod);

  const [occurredDate, setOccurredDate] = useState(todayIsoDate());

  // Локальный лок: ставим в true СИНХРОННО в обработчике клика — не ждём
  // React-rerender и не позволяем второму нажатию MainButton проскочить.
  const [submitting, setSubmitting] = useState(false);
  const minor = parseAmountToMinor(amount);
  const isValid = minor !== null && minor > 0n;
  const busy = loading || submitting;

  const cta = useMainButton({
    text: isPrepayment ? 'Записать досрочку' : 'Записать платёж',
    onClick: () => {
      if (!isValid || !minor || busy) return;
      setSubmitting(true);
      onSubmit({
        amountMinor: minor,
        periodYm: isPrepayment ? null : periodYm,
        occurredAt: new Date(`${occurredDate}T12:00:00.000Z`).toISOString(),
      });
    },
    enabled: isValid && !busy,
    progress: busy,
  });

  return (
    <div className="sheet-backdrop" onClick={onCancel} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">
          {isPrepayment ? 'Досрочка' : '+ Платёж'} · {debt.title}
        </div>

        <div className="sheet-stat-grid">
          <div className="sheet-stat">
            <div className="sheet-stat-label">Текущий остаток</div>
            <div className="sheet-stat-value">
              {formatMoney(debt.principal_remaining_minor, debt.currency)}
            </div>
          </div>
          <div className="sheet-stat">
            <div className="sheet-stat-label">Стандартный платёж</div>
            <div className="sheet-stat-value">
              {formatMoney(debt.monthly_payment_minor, debt.currency)}
            </div>
          </div>
        </div>

        <label className="form-field">
          <span className="form-label">Сумма ({debt.currency})</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={`form-input ${!isValid && amount ? 'invalid' : ''}`}
            disabled={busy}
            autoFocus
          />
          {!isValid && amount ? (
            <span className="form-hint error">Введи положительное число</span>
          ) : null}
        </label>

        {!isPrepayment ? (
          <label className="form-field">
            <span className="form-label">За какой месяц</span>
            <select
              value={periodYm ?? ''}
              onChange={(e) => setPeriodYm(e.target.value || null)}
              className="form-select"
              disabled={busy}
            >
              <option value="">Не указывать</option>
              {periodOptions.map((p) => (
                <option key={p} value={p}>
                  {formatPeriodLabel(p)}
                  {debt.overdue_periods?.includes(p) ? ' — просрочен' : ''}
                </option>
              ))}
            </select>
            <span className="form-hint">
              {periodYm
                ? 'Этот месяц будет помечен как оплаченный в истории долга.'
                : 'Без привязки к месяцу — просто зафиксировать факт платежа.'}
            </span>
          </label>
        ) : null}

        <label className="form-field">
          <span className="form-label">Дата выполнения</span>
          <input
            type="date"
            value={occurredDate}
            onChange={(e) => setOccurredDate(e.target.value)}
            className="form-input"
            disabled={busy}
          />
          <span className="form-hint">По умолчанию — сегодня.</span>
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
